import React, { useState, useEffect, useMemo, useRef } from 'react';
import { vansAPI, warehouseAPI, itemsAPI } from '../../lib/api';
import { AppDrawer } from '../ui/AppDrawer';
import { Button } from '../ui/button';
import { IntegerQuantityInput } from '../IntegerQuantityInput';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRightLeft, Loader2, Warehouse, Truck, Search } from 'lucide-react';
import { toast } from 'sonner';

function ItemSearchPicker({ items, value, onSelect, disabled }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selected = items.find(i => (i.item_id || i.id) === value);

  // When value changes externally (preselection), set search to item name
  useEffect(() => {
    if (selected && !isOpen) {
      setSearch(selected.item_name || selected.name || '');
    }
  }, [selected, isOpen]);

  const filtered = useMemo(() => {
    if (!search.trim() || (selected && search === (selected.item_name || selected.name))) {
      return items;
    }
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.item_name || i.name || '').toLowerCase().includes(q) ||
      (i.sku || '').toLowerCase().includes(q)
    );
  }, [items, search, selected]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        // Restore selected name if user didn't pick
        if (selected) setSearch(selected.item_name || selected.name || '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selected]);

  const handleSelect = (item) => {
    const id = item.item_id || item.id;
    onSelect(id);
    setSearch(item.item_name || item.name || '');
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') { setIsOpen(true); return; }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(p => (p + 1) % filtered.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(p => (p - 1 + filtered.length) % filtered.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < filtered.length) handleSelect(filtered[highlightIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default: break;
    }
  };

  return (
    <div className="space-y-2 min-w-0">
      <Label className="text-sm font-bold uppercase tracking-wide">Item</Label>
      <div className="relative" data-testid="transfer-item-picker">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); setHighlightIndex(-1); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search by name or SKU..."
          disabled={disabled}
          className="flex h-12 w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          autoComplete="off"
          data-testid="transfer-item-search"
        />

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-[360px] overflow-y-auto"
            data-testid="transfer-item-dropdown"
          >
            {filtered.length === 0 ? (
              <p className="py-4 px-3 text-center text-sm text-muted-foreground">
                {items.length === 0 ? 'No items at this location.' : 'No matching items.'}
              </p>
            ) : (
              filtered.map((item, idx) => {
                const itemId = item.item_id || item.id;
                const isSelected = value === itemId;
                return (
                  <button
                    key={itemId}
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={`w-full px-3 py-2.5 text-left flex flex-col gap-0.5 border-b border-border/50 last:border-b-0 transition-colors ${
                      highlightIndex === idx ? 'bg-accent' : isSelected ? 'bg-accent/50' : 'hover:bg-muted'
                    }`}
                    data-testid={`transfer-item-option-${itemId}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{item.item_name || item.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{item.qty_on_hand} avail</span>
                    </div>
                    {item.sku && (
                      <span className="text-[11px] text-muted-foreground/70">SKU: {item.sku}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const TransferModal = ({ 
  open, 
  onOpenChange, 
  onSuccess,
  preselectedItem = null,
  preselectedFromLocation = null,
  preselectedFromType = null,
  preselectedFromName = null,
  sourceReadOnly = false
}) => {
  const [vans, setVans] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouseInventory, setWarehouseInventory] = useState([]);
  const [vanInventory, setVanInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [fromType, setFromType] = useState(preselectedFromType || 'warehouse');
  const [fromLocationId, setFromLocationId] = useState(preselectedFromLocation || '');
  const [toType, setToType] = useState('vehicle');
  const [toLocationId, setToLocationId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState(preselectedItem?.id || '');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  // Fix: Reliably preselect the item when drawer opens
  useEffect(() => {
    if (open && preselectedItem) {
      setSelectedItemId(preselectedItem.id || preselectedItem.item_id || '');
    }
  }, [open, preselectedItem]);

  useEffect(() => {
    setToType(fromType === 'warehouse' ? 'vehicle' : 'warehouse');
    setToLocationId('');
  }, [fromType]);

  useEffect(() => {
    if (fromType === 'vehicle' && fromLocationId) fetchVanInventory(fromLocationId);
  }, [fromType, fromLocationId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vansRes, itemsRes, warehouseRes, warehousesRes] = await Promise.all([
        vansAPI.list(),
        itemsAPI.list({ page_size: 200 }),
        warehouseAPI.getInventory({ page_size: 200 }),
        warehouseAPI.listWarehouses()
      ]);
      const itemsData = itemsRes.data;
      const warehouseData = warehouseRes.data;
      setVans(vansRes.data);
      setItems(itemsData.items || itemsData);
      setWarehouseInventory(warehouseData.items || warehouseData);
      setWarehouses(warehousesRes.data || []);
      if (preselectedFromType) setFromType(preselectedFromType);
      if (preselectedFromLocation) setFromLocationId(preselectedFromLocation);
      if (preselectedItem) setSelectedItemId(preselectedItem.id || preselectedItem.item_id);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVanInventory = async (vanId) => {
    try {
      const response = await vansAPI.getInventory(vanId);
      setVanInventory(response.data);
    } catch (error) {
      console.error('Failed to load van inventory');
    }
  };

  const getAvailableItems = () => {
    if (fromType === 'warehouse') return warehouseInventory.filter(i => i.qty_on_hand > 0);
    if (fromType === 'vehicle' && fromLocationId) return vanInventory.filter(i => i.qty_on_hand > 0);
    return [];
  };

  const getMaxQuantity = () => {
    const selected = getAvailableItems().find(i => (i.item_id || i.id) === selectedItemId);
    return selected?.qty_on_hand || 0;
  };

  const resetForm = () => {
    if (!sourceReadOnly) {
      setFromType('warehouse');
      setFromLocationId('');
    }
    setToType('vehicle');
    setToLocationId('');
    setSelectedItemId('');
    setQuantity('1');
    setNote('');
    setVanInventory([]);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const qty = parseInt(quantity);
    if (qty <= 0 || qty > getMaxQuantity()) {
      toast.error('Invalid quantity');
      return;
    }
    setSaving(true);
    try {
      await warehouseAPI.transfer({
        from_location_type: fromType,
        from_location_id: fromType === 'warehouse'
          ? (sourceReadOnly ? preselectedFromLocation : fromLocationId)
          : fromLocationId,
        to_location_type: toType,
        to_location_id: toLocationId,
        item_id: selectedItemId,
        quantity: qty,
        note: note.trim() || null
      });
      toast.success('Transfer completed successfully');
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const availableItems = getAvailableItems();
  const maxQty = getMaxQuantity();

  const isDirty = selectedItemId !== '' || note !== '' || quantity !== '1';

  return (
    <AppDrawer
      open={open}
      onOpenChange={handleClose}
      title={
        <span className="flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5" />
          Transfer Item
        </span>
      }
      description="Transfer items between locations"
      size="md"
      isDirty={isDirty}
      data-testid="transfer-modal"
      footer={
        <>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={saving} data-testid="transfer-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              saving ||
              !selectedItemId ||
              parseInt(quantity) <= 0 ||
              parseInt(quantity) > maxQty ||
              !toLocationId ||
              (fromType === 'vehicle' && !fromLocationId) ||
              (fromType === 'warehouse' && !sourceReadOnly && warehouses.length > 1 && !fromLocationId)
            }
            data-testid="transfer-submit-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transferring...</>
            ) : (
              <><ArrowRightLeft className="w-4 h-4 mr-2" />Transfer</>
            )}
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Transfer From */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Transfer From</Label>
            {sourceReadOnly ? (
              <div className="h-12 px-4 rounded-md border bg-muted/50 flex items-center gap-2 text-muted-foreground" data-testid="transfer-from-readonly">
                {fromType === 'warehouse' ? <Warehouse className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                <span>{preselectedFromName || (fromType === 'warehouse' ? 'Warehouse' : 'Vehicle')}</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant={fromType === 'warehouse' ? 'default' : 'outline'} className="h-12"
                    onClick={() => setFromType('warehouse')} data-testid="transfer-from-warehouse-btn">
                    <Warehouse className="w-4 h-4 mr-2" />Warehouse
                  </Button>
                  <Button type="button" variant={fromType === 'vehicle' ? 'default' : 'outline'} className="h-12"
                    onClick={() => setFromType('vehicle')} data-testid="transfer-from-vehicle-btn">
                    <Truck className="w-4 h-4 mr-2" />Vehicle
                  </Button>
                </div>
                {fromType === 'warehouse' && warehouses.length > 1 && (
                  <Select value={fromLocationId} onValueChange={setFromLocationId}>
                    <SelectTrigger className="h-12" data-testid="transfer-from-warehouse-select">
                      <SelectValue placeholder="Select source warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.filter(w => !w.archived_at).map(wh => (
                        <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {fromType === 'vehicle' && (
                  <Select value={fromLocationId} onValueChange={setFromLocationId}>
                    <SelectTrigger className="h-12" data-testid="transfer-from-vehicle-select">
                      <SelectValue placeholder="Select source vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vans.filter(v => v.is_active && !v.archived_at).map(van => (
                        <SelectItem key={van.id} value={van.id}>{van.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </>
            )}
          </div>

          {/* Item Selection — Searchable Picker */}
          <ItemSearchPicker
            items={availableItems}
            value={selectedItemId}
            onSelect={setSelectedItemId}
            disabled={fromType === 'vehicle' && !fromLocationId}
          />

          {/* Quantity */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">
              Quantity {maxQty > 0 && <span className="text-muted-foreground font-normal">(max: {maxQty})</span>}
            </Label>
            <IntegerQuantityInput min={1} max={maxQty} value={quantity}
              onChange={(val) => setQuantity(val)} className="h-12 text-lg font-mono"
              data-testid="transfer-quantity-input" />
          </div>

          {/* Transfer To */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Transfer To</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={toType === 'warehouse' ? 'default' : 'outline'} className="h-12"
                onClick={() => { setToType('warehouse'); setToLocationId(''); }} data-testid="transfer-to-warehouse-btn">
                <Warehouse className="w-4 h-4 mr-2" />Warehouse
              </Button>
              <Button type="button" variant={toType === 'vehicle' ? 'default' : 'outline'} className="h-12"
                onClick={() => { setToType('vehicle'); setToLocationId(''); }} data-testid="transfer-to-vehicle-btn">
                <Truck className="w-4 h-4 mr-2" />Vehicle
              </Button>
            </div>
            {toType === 'warehouse' && (
              <Select value={toLocationId} onValueChange={setToLocationId}>
                <SelectTrigger className="h-12" data-testid="transfer-to-warehouse-select">
                  <SelectValue placeholder="Select destination warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => !w.archived_at && w.id !== fromLocationId).map(wh => (
                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {toType === 'vehicle' && (
              <Select value={toLocationId} onValueChange={setToLocationId}>
                <SelectTrigger className="h-12" data-testid="transfer-to-vehicle-select">
                  <SelectValue placeholder="Select destination vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vans.filter(v => v.id !== fromLocationId && v.is_active && !v.archived_at).map(van => (
                    <SelectItem key={van.id} value={van.id}>{van.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note about this transfer..." rows={2} className="resize-none"
              data-testid="transfer-note-input" />
          </div>
        </div>
      )}
    </AppDrawer>
  );
};
